import { toDocument } from '@littlebird/action-cards';
import {
  ENTITY_REFERENCE_PATTERN,
  parseEntityReference,
  type EntityReference as EntityReferenceSyntax,
} from '@littlebird/markdown-utils';
import { InputRule, Node, PasteRule, type JSONContent } from '@tiptap/core';
import Text from '@tiptap/extension-text';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { isSupportedEntityReference } from '@/utils/entity-reference';

import { EntityReferenceNodeView } from '../components/EntityReferenceNodeView';

const ENTITY_REFERENCE_INPUT_PATTERN = new RegExp(
  `${ENTITY_REFERENCE_PATTERN.source}$`
);
const ENTITY_REFERENCE_SCAN_PATTERN = new RegExp(
  ENTITY_REFERENCE_PATTERN.source,
  'g'
);

export const entityReferenceDocument = (
  text: string,
  document?: JSONContent
): JSONContent => {
  const convertNode = (node: JSONContent): JSONContent[] => {
    if (node.type === 'routineReference')
      return convertNode({
        type: 'text',
        text: `::routine{id=${node.attrs?.id}}`,
        marks: node.marks,
      });

    if (
      node.type === 'codeBlock' ||
      node.marks?.some((mark) => mark.type === 'code')
    )
      return [node];

    if (node.type === 'entityReference') {
      const reference = parseEntityReference(node.attrs?.raw ?? '');

      return isSupportedEntityReference(reference)
        ? [{ ...node, attrs: reference }]
        : [{ type: 'text', text: node.attrs?.raw ?? '', marks: node.marks }];
    }

    if (node.content)
      return [{ ...node, content: node.content.flatMap(convertNode) }];

    if (!node.text) return [node];

    const content: JSONContent[] = [];
    let offset = 0;

    for (const match of node.text.matchAll(ENTITY_REFERENCE_SCAN_PATTERN)) {
      const reference = parseEntityReference(match[0]);
      if (!isSupportedEntityReference(reference)) continue;

      if (match.index > offset)
        content.push({ ...node, text: node.text.slice(offset, match.index) });

      content.push({
        type: 'entityReference',
        attrs: reference,
        marks: node.marks,
      });
      offset = match.index + match[0].length;
    }

    if (offset < node.text.length)
      content.push({ ...node, text: node.text.slice(offset) });

    return content;
  };

  return convertNode(document ?? toDocument(text))[0];
};

export const EntityReference = Node.create({
  name: 'entityReference',
  priority: 110,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes: () => ({
    entity: { default: '', rendered: false },
    attributes: { default: {}, rendered: false },
    raw: {
      default: '',
      renderHTML: ({ raw }) => ({ 'data-entity-reference': raw }),
    },
  }),

  parseHTML: () => [
    {
      tag: 'span[data-entity-reference]',
      getAttrs: (element) => {
        const reference = parseEntityReference(
          element.getAttribute('data-entity-reference') ?? ''
        );

        return isSupportedEntityReference(reference) ? reference : false;
      },
    },
  ],

  renderHTML: ({ node, HTMLAttributes }) => [
    'span',
    HTMLAttributes,
    node.attrs.raw,
  ],

  renderText: ({ node }) => node.attrs.raw,

  addStorage: () => ({
    markdown: {
      serialize: (
        state: { write(text: string): void },
        node: { attrs: EntityReferenceSyntax }
      ) => state.write(node.attrs.raw),
    },
  }),

  addNodeView: () => ReactNodeViewRenderer(EntityReferenceNodeView),

  addInputRules() {
    return [
      new InputRule({
        find: ENTITY_REFERENCE_INPUT_PATTERN,
        handler: ({ state, range, match }) => {
          const reference = parseEntityReference(match[0]);
          if (!isSupportedEntityReference(reference)) return null;

          state.tr.replaceWith(
            range.from,
            range.to,
            this.type.create(reference)
          );
        },
      }),
    ];
  },

  addPasteRules() {
    return [
      new PasteRule({
        find: ENTITY_REFERENCE_SCAN_PATTERN,
        handler: ({ state, range, match }) => {
          const reference = parseEntityReference(match[0]);
          if (!isSupportedEntityReference(reference)) return null;

          if (
            state.doc
              .resolve(range.from)
              .marks()
              .some((mark) => mark.type.spec.code)
          )
            return null;

          state.tr.replaceWith(
            range.from,
            range.to,
            this.type.create(reference)
          );
        },
      }),
    ];
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () => {
        const { empty, $from } = this.editor.state.selection;

        if (!empty || $from.nodeBefore?.type !== this.type) return false;

        return this.editor.commands.deleteRange({
          from: $from.pos - 1,
          to: $from.pos,
        });
      },
    };
  },
});

export const EntityReferenceText = Text.extend({
  addStorage: () => ({
    markdown: {
      serialize(
        state: { text(text: string, escape?: boolean): void },
        node: { text: string }
      ) {
        const writeText = (text: string) =>
          state.text(text.replace(/</g, '&lt;').replace(/>/g, '&gt;'));

        let offset = 0;

        for (const match of node.text.matchAll(ENTITY_REFERENCE_SCAN_PATTERN)) {
          const reference = parseEntityReference(match[0]);
          if (!reference) continue;

          writeText(node.text.slice(offset, match.index));
          state.text(reference.raw, false);
          offset = match.index + match[0].length;
        }

        writeText(node.text.slice(offset));
      },
    },
  }),
});
